import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from 'recharts';
import type { ChartData } from '../../types/agent.types';

/** Named colour palettes */
export const CHART_PALETTES: Record<string, string[]> = {
  default: ['#7C7CFF', '#38BDF8', '#34D399', '#FBBF24', '#F472B6', '#A8A2FF'],
  boroondara: ['#2e873d', '#55a655', '#7BC47B', '#A8DCA8', '#D4F0D4', '#1a5c26'],
  ocean: ['#0077B6', '#00B4D8', '#48CAE4', '#90E0EF', '#ADE8F4', '#CAF0F8'],
  sunset: ['#E76F51', '#F4A261', '#E9C46A', '#2A9D8F', '#264653', '#E07A5F'],
  corporate: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#1E40AF'],
};

const COLORS = CHART_PALETTES.default;

export type ChartPanelChartType = 'bar' | 'pie' | 'line' | 'donut' | 'area';

interface ChartPanelProps {
  data: ChartData;
  chartType: ChartPanelChartType;
  height?: number;
  palette?: string[] | string;
  animate?: boolean;
  embedded?: boolean;
  legendLabels?: Record<string, string>;
  /** When provided, clicking a bar or pie segment issues a drilldown on the
   *  chart's category (x-axis) value. Receives the human-readable category
   *  label and the clicked value. */
  onDrilldown?: (field: string, value: string) => void;
}

// Column names containing these hints make good chart labels
const LABEL_HINTS = [
  'name', 'code', 'type', 'status', 'category', 'department',
  'classification', 'period', 'source', 'method', 'priority',
  'label', 'title', 'description', 'suburb', 'area', 'region',
  'district', 'supplier', 'vendor', 'account', 'cost_centre',
  'month', 'year', 'date', 'quarter',
];

// Column name patterns that indicate ID/key columns (should be excluded from charts)
const ID_COL_PATTERNS = [
  /^id$/i, /_id$/i, /^guid$/i, /^uuid$/i, /_guid$/i, /_uuid$/i,
  /_key$/i, /^key$/i, /_hash$/i, /^hash$/i, /_token$/i, /^token$/i,
  /^pk_/i, /^fk_/i, /^ref_/i, /_ref$/i, /^oid$/i, /_oid$/i,
];

// UUID pattern for detecting UUID values in data
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Broader pattern: hex strings that look like hashes/IDs (8+ hex chars)
const HEX_ID_RE = /^[0-9a-f]{8,}$/i;

// Numeric-only IDs (long integers that are clearly row IDs, not meaningful values)
const NUMERIC_ID_RE = /^\d{6,}$/;

/**
 * Check if a value looks like a GUID, hash, or meaningless ID.
 */
function looksLikeId(value: string): boolean {
  if (UUID_RE.test(value)) return true;
  if (HEX_ID_RE.test(value) && value.length >= 12) return true;
  return false;
}

/**
 * Check if a column name matches ID/key patterns.
 */
function isIdColumnName(name: string): boolean {
  return ID_COL_PATTERNS.some((pat) => pat.test(name));
}

/**
 * Detect which columns are numeric vs categorical by inspecting actual row values.
 * Skips UUID/GUID columns, ID columns, and hash columns so they never appear on charts.
 */
function classifyColumns(data: ChartData) {
  const { fields, rows } = data;
  const numericCols: string[] = [];
  const categoricalCols: string[] = [];

  for (const field of fields) {
    const name = field.name;

    // Skip columns whose name matches ID/key patterns
    if (isIdColumnName(name)) continue;

    const sampleValues = rows.slice(0, 10).map((r) => r[name]);
    const nonNull = sampleValues.filter((v) => v !== null && v !== undefined && v !== '');

    // Skip columns where values look like UUIDs, hashes, or meaningless IDs
    if (nonNull.length > 0 && nonNull.every((v) => looksLikeId(String(v)))) continue;

    // Skip numeric columns where all values look like row IDs (long integers, high cardinality)
    if (nonNull.length > 0 && nonNull.every((v) => NUMERIC_ID_RE.test(String(v)))) {
      // Check if values are sequential-ish (likely row IDs)
      const nums = nonNull.map((v) => Number(v));
      const range = Math.max(...nums) - Math.min(...nums);
      if (range > 0 && range < nums.length * 10) continue; // Sequential IDs
    }

    const isNumeric =
      nonNull.length > 0 &&
      nonNull.every((v) => {
        if (typeof v === 'number') return true;
        const cleaned = String(v).replace(/[$,]/g, '');
        return !isNaN(Number(cleaned)) && cleaned !== '';
      });

    if (isNumeric) {
      numericCols.push(name);
    } else {
      categoricalCols.push(name);
    }
  }

  return { numericCols, categoricalCols };
}

/**
 * Pick the best label column: prefer columns whose name contains a
 * meaningful hint (name, code, type, status, category, etc.).
 * Falls back to the first categorical column, but never picks an ID-like column.
 */
function pickLabelCol(categoricalCols: string[], fallback: string): string {
  if (categoricalCols.length === 0) return fallback;

  // First pass: exact hint match
  const hinted = categoricalCols.find((c) =>
    LABEL_HINTS.some((h) => c.toLowerCase().includes(h))
  );
  if (hinted) return hinted;

  // Second pass: pick the first column that isn't ID-like
  const nonId = categoricalCols.find((c) => !isIdColumnName(c));
  return nonId || categoricalCols[0];
}

/**
 * Detect if a column contains currency values (starts with $ or has $ in most values).
 */
function isCurrencyColumn(rows: Record<string, any>[], colName: string): boolean {
  const samples = rows.slice(0, 10).map((r) => r[colName]);
  const withDollar = samples.filter(
    (v) => v !== null && v !== undefined && String(v).includes('$')
  );
  return withDollar.length > samples.length * 0.3;
}

/**
 * Make a human-readable axis label from a column name.
 */
function humanLabel(col: string): string {
  if (/[A-Z]/.test(col) && col.includes(' ')) return col;
  return col
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Prepare chart-ready data: ensure numeric values are actual numbers,
 * and truncate long category labels.
 */
function prepareData(
  rows: Record<string, any>[],
  labelCol: string,
  numericCols: string[]
) {
  return rows.map((row) => {
    const entry: Record<string, any> = {};
    const raw = row[labelCol] ?? '';
    const label = String(raw);
    entry[labelCol] = label.length > 28 ? label.slice(0, 26) + '..' : label;
    // Preserve the untruncated label so drilldown clicks query the real value.
    entry.__fullLabel = label;

    for (const col of numericCols) {
      const val = row[col];
      if (typeof val === 'number') {
        entry[col] = val;
      } else {
        const cleaned = String(val ?? '').replace(/[$,]/g, '');
        entry[col] = isNaN(Number(cleaned)) ? 0 : Number(cleaned);
      }
    }
    return entry;
  });
}

/**
 * Format large numbers with K/M suffixes for axis ticks.
 */
function formatAxisValue(value: number, isCurrency: boolean): string {
  const prefix = isCurrency ? '$' : '';
  if (Math.abs(value) >= 1_000_000) return `${prefix}${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${prefix}${(value / 1_000).toFixed(0)}K`;
  return `${prefix}${value}`;
}

/**
 * Format tooltip values with comma separators and optional currency.
 */
function formatTooltipValue(value: number, isCurrency: boolean): string {
  if (isCurrency) {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Resolve palette: string name or custom array */
function resolvePalette(palette?: string[] | string): string[] {
  if (!palette) return COLORS;
  if (Array.isArray(palette)) return palette;
  return CHART_PALETTES[palette] || COLORS;
}

/** Custom tooltip component for premium look */
function PremiumTooltip({ active, payload, label, isCurrency, isPie }: any) {
  if (!active || !payload || payload.length === 0) return null;

  const card: React.CSSProperties = {
    background: '#FFFFFF',
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    padding: '10px 14px',
    border: 'none',
  };

  // Pie / donut: a single slice. Recharts doesn't pass `label` here, so show
  // the slice name as the header (names live in the legend, not on the slice)
  // with the value and share-of-total beneath it.
  if (isPie) {
    const entry = payload[0];
    const pct = typeof entry.percent === 'number' ? ` (${Math.round(entry.percent * 100)}%)` : '';
    return (
      <div style={card}>
        <div style={{ fontWeight: 600, fontSize: 12, color: '#232323', marginBottom: 4 }}>
          {entry.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#484848' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.payload?.fill || entry.color, flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: '#232323' }}>
            {formatTooltipValue(entry.value, isCurrency)}{pct}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontWeight: 600, fontSize: 12, color: '#232323', marginBottom: 4 }}>
        {label}
      </div>
      {payload.map((entry: any, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#484848', marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
          <span>{humanLabel(entry.name)}:</span>
          <span style={{ fontWeight: 600, color: '#232323' }}>
            {formatTooltipValue(entry.value, isCurrency)}
          </span>
        </div>
      ))}
    </div>
  );
}

const embeddedStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  margin: 0,
  padding: '8px 0 0 0',
  borderRadius: 0,
};

export default function ChartPanel({ data, chartType, height = 360, palette, animate = true, embedded = false, legendLabels, onDrilldown }: ChartPanelProps) {
  // Normalise potentially malformed data so hooks always run
  const safeData = useMemo(() => ({
    fields: data?.fields || [],
    rows: data?.rows || [],
    rowCount: data?.rowCount || 0,
  }), [data]);

  const { numericCols, categoricalCols } = useMemo(() => classifyColumns(safeData), [safeData]);
  const colors = useMemo(() => resolvePalette(palette), [palette]);

  const labelCol = pickLabelCol(categoricalCols, safeData.fields[0]?.name || 'label');
  const valueCols = useMemo(
    () => (numericCols.length > 0 ? numericCols : [safeData.fields[1]?.name || 'value']),
    [numericCols, safeData.fields]
  );

  // Detect if primary value column is currency
  const primaryIsCurrency = useMemo(
    () => valueCols.length > 0 && isCurrencyColumn(safeData.rows, valueCols[0]),
    [safeData.rows, valueCols]
  );

  const chartData = useMemo(
    () => prepareData(safeData.rows, labelCol, valueCols),
    [safeData.rows, labelCol, valueCols]
  );

  // Human-readable axis labels
  const xLabel = humanLabel(labelCol);
  const yLabel = valueCols.length === 1 ? humanLabel(valueCols[0]) : '';

  // Compute total for donut centre label
  const pieTotal = useMemo(() => {
    if (chartType !== 'donut') return 0;
    return chartData.reduce((sum, row) => sum + (Number(row[valueCols[0]]) || 0), 0);
  }, [chartType, chartData, valueCols]);

  const activeStyle = embedded ? embeddedStyle : panelStyle;

  if (!chartData.length || valueCols.length === 0) {
    return (
      <div style={activeStyle}>
        <span style={{ color: '#747474', fontSize: 13 }}>
          No numeric data available to chart.
        </span>
      </div>
    );
  }

  const animDuration = animate ? 800 : 0;
  const yTickFormatter = (v: number) => formatAxisValue(v, primaryIsCurrency);

  /** Resolve legend/series label: custom legendLabels → humanLabel fallback */
  const seriesLabel = (col: string): string => legendLabels?.[col] || humanLabel(col);

  /** Fire a drilldown for the category under a clicked bar / pie segment.
   *  Recharts hands back either the chart entry or a wrapper with `.payload`,
   *  so we look in both for the preserved untruncated label. */
  const drillFromEntry = (entry: any) => {
    if (!onDrilldown) return;
    const full =
      entry?.payload?.__fullLabel ??
      entry?.__fullLabel ??
      entry?.payload?.[labelCol] ??
      entry?.[labelCol];
    if (full !== undefined && full !== null && String(full).trim() !== '') {
      onDrilldown(humanLabel(labelCol), String(full));
    }
  };
  const drillCursor = onDrilldown ? 'pointer' : undefined;

  const legendStyle = { color: '#484848', fontSize: 12, paddingTop: 8 };

  return (
    <div style={activeStyle}>
      <ResponsiveContainer width="100%" height={height}>
        {chartType === 'bar' ? (
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" vertical={false} />
            <XAxis
              dataKey={labelCol}
              tick={{ fill: '#747474', fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={70}
              interval={0}
            >
              <Label
                value={xLabel}
                position="insideBottom"
                offset={-5}
                style={{ fill: '#A0A0A0', fontSize: 11, fontWeight: 500 }}
              />
            </XAxis>
            <YAxis
              tick={{ fill: '#747474', fontSize: 11 }}
              tickFormatter={yTickFormatter}
              width={70}
            >
              {yLabel && (
                <Label
                  value={yLabel}
                  angle={-90}
                  position="insideLeft"
                  offset={0}
                  style={{ fill: '#A0A0A0', fontSize: 11, fontWeight: 500, textAnchor: 'middle' }}
                />
              )}
            </YAxis>
            <Tooltip content={<PremiumTooltip isCurrency={primaryIsCurrency} />} />
            {valueCols.length > 1 && (
              <Legend wrapperStyle={legendStyle} formatter={(v) => seriesLabel(v)} />
            )}
            {valueCols.map((col, i) => (
              <Bar
                key={col}
                dataKey={col}
                name={seriesLabel(col)}
                fill={colors[i % colors.length]}
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
                animationDuration={animDuration}
                animationEasing="ease-in-out"
                cursor={drillCursor}
                onClick={onDrilldown ? drillFromEntry : undefined}
              />
            ))}
          </BarChart>

        ) : chartType === 'line' ? (
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" vertical={false} />
            <XAxis
              dataKey={labelCol}
              tick={{ fill: '#747474', fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={70}
              interval={0}
            >
              <Label
                value={xLabel}
                position="insideBottom"
                offset={-5}
                style={{ fill: '#A0A0A0', fontSize: 11, fontWeight: 500 }}
              />
            </XAxis>
            <YAxis
              tick={{ fill: '#747474', fontSize: 11 }}
              tickFormatter={yTickFormatter}
              width={70}
            >
              {yLabel && (
                <Label
                  value={yLabel}
                  angle={-90}
                  position="insideLeft"
                  offset={0}
                  style={{ fill: '#A0A0A0', fontSize: 11, fontWeight: 500, textAnchor: 'middle' }}
                />
              )}
            </YAxis>
            <Tooltip content={<PremiumTooltip isCurrency={primaryIsCurrency} />} />
            {valueCols.length > 1 && (
              <Legend wrapperStyle={legendStyle} formatter={(v) => seriesLabel(v)} />
            )}
            {valueCols.map((col, i) => (
              <Line
                key={col}
                type="monotone"
                dataKey={col}
                name={seriesLabel(col)}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
                dot={{ r: 3, fill: colors[i % colors.length] }}
                activeDot={{ r: 5 }}
                animationDuration={animDuration}
                animationEasing="ease-in-out"
              />
            ))}
          </LineChart>

        ) : chartType === 'area' ? (
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 50 }}>
            <defs>
              {valueCols.map((col, i) => (
                <linearGradient key={col} id={`area-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors[i % colors.length]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={colors[i % colors.length]} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" vertical={false} />
            <XAxis
              dataKey={labelCol}
              tick={{ fill: '#747474', fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={70}
              interval={0}
            >
              <Label
                value={xLabel}
                position="insideBottom"
                offset={-5}
                style={{ fill: '#A0A0A0', fontSize: 11, fontWeight: 500 }}
              />
            </XAxis>
            <YAxis
              tick={{ fill: '#747474', fontSize: 11 }}
              tickFormatter={yTickFormatter}
              width={70}
            >
              {yLabel && (
                <Label
                  value={yLabel}
                  angle={-90}
                  position="insideLeft"
                  offset={0}
                  style={{ fill: '#A0A0A0', fontSize: 11, fontWeight: 500, textAnchor: 'middle' }}
                />
              )}
            </YAxis>
            <Tooltip content={<PremiumTooltip isCurrency={primaryIsCurrency} />} />
            {valueCols.length > 1 && (
              <Legend wrapperStyle={legendStyle} formatter={(v) => seriesLabel(v)} />
            )}
            {valueCols.map((col, i) => (
              <Area
                key={col}
                type="monotone"
                dataKey={col}
                name={seriesLabel(col)}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
                fill={`url(#area-grad-${i})`}
                animationDuration={animDuration}
                animationEasing="ease-in-out"
              />
            ))}
          </AreaChart>

        ) : (
          /* Pie / Donut chart */
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={chartData}
              dataKey={valueCols[0]}
              nameKey={labelCol}
              cx="50%"
              cy="50%"
              outerRadius={Math.min(height * 0.38, 130)}
              innerRadius={chartType === 'donut' ? Math.min(height * 0.22, 72) : 0}
              paddingAngle={2}
              cursor={drillCursor}
              onClick={onDrilldown ? drillFromEntry : undefined}
              label={renderPiePercentLabel}
              labelLine={false}
              animationDuration={animDuration}
              animationEasing="ease-in-out"
            >
              {chartData.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            {/* Donut centre label */}
            {chartType === 'donut' && (
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: 16, fontWeight: 700, fill: '#232323' }}
              >
                {primaryIsCurrency
                  ? '$' + (pieTotal >= 1_000_000
                      ? `${(pieTotal / 1_000_000).toFixed(1)}M`
                      : pieTotal >= 1000
                        ? `${(pieTotal / 1000).toFixed(0)}K`
                        : pieTotal.toLocaleString())
                  : pieTotal.toLocaleString()}
              </text>
            )}
            <Tooltip
              content={<PremiumTooltip isCurrency={primaryIsCurrency} isPie />}
            />
            <Legend wrapperStyle={legendStyle} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Pie/donut slice label — renders the percentage INSIDE the slice (white,
 * centred on the ring) instead of long leader-line labels outside the chart,
 * which overflowed and clipped on narrow widgets. Slice names come from the
 * legend and full values from the tooltip. Tiny slices (<5%) are skipped to
 * avoid crowding.
 */
function renderPiePercentLabel(props: any) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (!percent || percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 700, pointerEvents: 'none' }}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
}

const panelStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '16px 8px 8px 8px',
  borderRadius: 4,
  background: '#FAFAFA',
  border: '1px solid #EDEDED',
};
